import React, { useState, useEffect, useCallback } from 'react';
import { postalCodeBody } from '@carboncare/schemas';
import { preflight } from '../../services/api/schemaPreflight';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';
import { useCoordinatePaste } from '../../hooks/useCoordinatePaste';
import PageHeader from '../../components/common/PageHeader';
import {
  StatusSnackbars,
  CountChip,
  usePaginationState,
  useReferenceSearch,
  SearchToolbar,
  NoResultsRow,
  CrudTablePagination,
  DeleteConfirmDialog,
  SkeletonActionCell,
  EditDeleteActions,
  CoordinatesCaption,
  makeLatLonFormChangeHandler,
  CrudFormDialog,
  LatLonFields,
  validateDecimal,
} from './_shared';

interface PostalCode {
  id?: number;
  postal_code: string;
  city?: string;
  state?: string;
  state_province?: string;
  country?: string;
  country_code?: string;
  country_name?: string;
  lat: number;
  lon: number;
  created_at?: string;
  updated_at?: string;
}

const PostalCodesPage: React.FC = () => {
  const [postalCodes, setPostalCodes] = useState<PostalCode[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const { page, setPage, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePaginationState(20);
  const { search, matchMode, searchProps } = useReferenceSearch(setPage);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [selectedPostalCode, setSelectedPostalCode] = useState<PostalCode | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postalCodeToDelete, setPostalCodeToDelete] = useState<PostalCode | null>(null);

  const [formData, setFormData] = useState<Partial<PostalCode>>({
    postal_code: '',
    city: '',
    state_province: '',
    country_code: '',
    lat: undefined,
    lon: undefined
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handlePasteCoordinates = useCoordinatePaste(setFormData, setFormErrors);

  const handleFormChange = makeLatLonFormChangeHandler(setFormData, setFormErrors);

  const fetchPostalCodes = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get('/data-management/postal-codes', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          matchMode,
          country: selectedCountry,
          sortBy: 'postal_code',
          sortOrder: 'asc'
        }
      });

      if (response.data.success) {
        setPostalCodes(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch postal codes');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, matchMode, selectedCountry]);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await apiClient.get('/data-management/postal-codes/countries');
      if (response.data.success) {
        setCountries(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch countries:', err);
    }
  }, []);

  useEffect(() => {
    fetchPostalCodes();
  }, [fetchPostalCodes]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const handleCountryChange = (event: any) => {
    setSelectedCountry(event.target.value);
    setPage(0);
  };

  const handleOpenDialog = (mode: 'add' | 'edit', postalCode?: PostalCode) => {
    setDialogMode(mode);
    if (mode === 'edit' && postalCode) {
      setSelectedPostalCode(postalCode);
      setFormData({
        postal_code: postalCode?.postal_code || '',
        city: postalCode?.city || '',
        state_province: postalCode?.state_province || postalCode?.state || '',
        country_code: postalCode?.country_code || postalCode?.country || '',
        lat: postalCode?.lat || 0,
        lon: postalCode?.lon || 0
      });
    } else {
      setSelectedPostalCode(null);
      setFormData({
        postal_code: '',
        city: '',
        state_province: '',
        country_code: '',
        lat: undefined,
        lon: undefined
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPostalCode(null);
    setFormErrors({});
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.postal_code) newErrors.postal_code = 'Postal Code is required';
    if (!formData.country_code) newErrors.country_code = 'Country Code is required';
    const latError = validateDecimal(formData.lat, 'Latitude');
    const lonError = validateDecimal(formData.lon, 'Longitude');
    if (latError) newErrors.lat = latError;
    if (lonError) newErrors.lon = lonError;

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      if (dialogMode === 'add') {
        preflight(postalCodeBody, formData);
        const response = await apiClient.post('/data-management/postal-codes', formData);
        if (response.data.success) {
          setSuccess('Postal code added successfully');
          fetchPostalCodes();
        }
      } else {
        const response = await apiClient.put(
          `/data-management/postal-codes/${selectedPostalCode?.id}`,
          formData
        );
        if (response.data.success) {
          setSuccess('Postal code updated successfully');
          fetchPostalCodes();
        }
      }
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save postal code');
    }
  };

  const handleDeleteClick = (postalCode: PostalCode) => {
    setPostalCodeToDelete(postalCode);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!postalCodeToDelete) return;

    try {
      const response = await apiClient.delete(
        `/data-management/postal-codes/${postalCodeToDelete.id}`
      );
      if (response.data.success) {
        setSuccess('Postal code deleted successfully');
        fetchPostalCodes();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete postal code');
    } finally {
      setDeleteDialogOpen(false);
      setPostalCodeToDelete(null);
    }
  };

  return (
    <Box>
      <PageHeader
        description="View and maintain the postal code reference dataset."
        actions={
          <>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchPostalCodes}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => handleOpenDialog('add')}
            >
              Add Postal Code
            </Button>
          </>
        }
      />

      <StatusSnackbars
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />

      <Paper sx={{ p: 2, mb: 3 }}>
        <SearchToolbar
          placeholder="Search by postal code, city, or state..."
          {...searchProps}
          countSlot={<CountChip loading={loading} count={totalCount} label="postal codes" />}
          rightSlot={
            <FormControl sx={{ minWidth: 200, margin: 0 }}>
              <InputLabel>Filter by Country</InputLabel>
              <Select
                value={selectedCountry}
                label="Filter by Country"
                onChange={handleCountryChange}
              >
                <MenuItem value="">All Countries</MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Postal Code</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Coordinates</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 10 }, (_, i) => `skeleton-${i}`).map((rowKey, idx) => (
                  <TableRow key={rowKey}>
                    <TableCell><Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                    <TableCell><Skeleton variant="text" width={`${50 + ((idx * 9) % 30)}%`} /></TableCell>
                    <TableCell><Skeleton variant="text" width={110} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={48} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                    <TableCell><Skeleton variant="text" width={130} sx={{ fontSize: '0.75rem' }} /></TableCell>
                    <SkeletonActionCell />
                  </TableRow>
                ))
              : postalCodes.length === 0
                ? <NoResultsRow colSpan={6} search={search} matchMode={matchMode} />
                : postalCodes.map((postalCode) => (
                  <TableRow key={postalCode.id} hover>
                    <TableCell>
                      <Chip label={postalCode.postal_code} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{postalCode.city || '-'}</TableCell>
                    <TableCell>{postalCode.state_province || postalCode.state || '-'}</TableCell>
                    <TableCell>
                      <Chip label={postalCode.country_code || '-'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <CoordinatesCaption lat={postalCode.lat} lon={postalCode.lon} />
                    </TableCell>
                    <EditDeleteActions
                      onEdit={() => handleOpenDialog('edit', postalCode)}
                      onDelete={() => handleDeleteClick(postalCode)}
                    />
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <CrudTablePagination
          loading={loading}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <CrudFormDialog
        open={openDialog}
        title={dialogMode === 'add' ? 'Add New Postal Code' : 'Edit Postal Code'}
        onClose={handleCloseDialog}
        onSave={handleSave}
        saveLabel={dialogMode === 'add' ? 'Add' : 'Update'}
      >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.postal_code}
                onChange={handleFormChange('postal_code')}
                required
                error={!!formErrors.postal_code}
                helperText={formErrors.postal_code}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={handleFormChange('city')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="State/Province"
                value={formData.state_province || ''}
                onChange={handleFormChange('state_province')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Country Code (e.g. US, AU, CA)"
                value={formData.country_code || ''}
                onChange={handleFormChange('country_code')}
                required
                error={!!formErrors.country_code}
                helperText={formErrors.country_code}
              />
            </Grid>
            <LatLonFields
              latValue={formData.lat}
              lonValue={formData.lon}
              onLatChange={handleFormChange('lat')}
              onLonChange={handleFormChange('lon')}
              onPaste={handlePasteCoordinates}
              latError={formErrors.lat}
              lonError={formErrors.lon}
            />
          </Grid>
      </CrudFormDialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Are you sure you want to delete postal code "{postalCodeToDelete?.postal_code}"
        ({postalCodeToDelete?.city}, {postalCodeToDelete?.country})?
      </DeleteConfirmDialog>
    </Box>
  );
};

export default PostalCodesPage;
